import { useFormikContext } from "formik";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { FormikCell, FormikMoneyCell } from "@/features/forms/components/formik-controls";
import type { Form8857Data } from "@/features/forms/types";
import { otherAssetsApi } from "@/features/worksheets/api";
import {
  type Column,
  CollectionCard,
} from "@/features/worksheets/components/collection-card";
import { OtherAssetDialog } from "@/features/worksheets/components/dialogs/other-asset-dialog";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import { formatMoney } from "@/features/worksheets/money";
import type { OtherAsset } from "@/features/worksheets/types";
import { EXPENSE_VALUE_KEYS, INCOME_VALUE_KEYS } from "../fields";

const INCOME_ROWS: { name: keyof Form8857Data; label: string }[] = [
  { name: "month_gifts", label: "Gifts" },
  { name: "month_wages", label: "Wages (gross pay)" },
  { name: "month_pensions", label: "Pensions" },
  { name: "month_unemployment", label: "Unemployment" },
  { name: "month_social_security", label: "Social security" },
  { name: "month_government_assistant", label: "Government assistance" },
  { name: "month_alimony", label: "Alimony" },
  { name: "month_child_support", label: "Child support" },
  { name: "month_self_employment_business_income", label: "Self-employment business income" },
  { name: "month_rental_income", label: "Rental income" },
  { name: "month_interest_dividend", label: "Interest and dividends" },
];
const INCOME_OTHER: { type: keyof Form8857Data; val: keyof Form8857Data }[] = [
  { type: "other_income_type1", val: "other_income_type_val1" },
  { type: "other_income_type2", val: "other_income_type_val2" },
  { type: "other_income_type3", val: "other_income_type_val3" },
];

const EXPENSE_ROWS: { name: keyof Form8857Data; label: string }[] = [
  { name: "month_exp_food", label: "Food" },
  { name: "month_exp_house_supply", label: "Housekeeping supplies" },
  { name: "month_exp_clothing_services", label: "Clothing and clothing services" },
  { name: "month_exp_personel_care", label: "Personal care products and services" },
  { name: "month_exp_auto_loan", label: "Auto loan/lease, gas, insurance, etc." },
  { name: "month_exp_pub_trans", label: "Public transportation" },
  { name: "month_exp_rent_mort", label: "Rent or mortgage" },
  { name: "month_exp_real_estate", label: "Real estate taxes and insurance" },
  { name: "month_exp_electric_gas", label: "Electric, oil, gas, water, trash" },
  { name: "month_exp_telephone", label: "Telephone and cell phone" },
  { name: "month_exp_cab_int", label: "Cable and Internet" },
  { name: "month_exp_health", label: "Health insurance premiums" },
  { name: "month_exp_out_pocket", label: "Out-of-pocket medical" },
  { name: "month_exp_other_child_dep", label: "Child and dependent care" },
  { name: "month_exp_other_care", label: "Caregiver expenses" },
  { name: "month_exp_other_income_tax", label: "Income tax withholding" },
  { name: "month_exp_other_estimate_tax", label: "Estimated tax payments" },
  { name: "month_exp_other_term_insur", label: "Term life insurance premiums" },
  { name: "month_exp_other_retr_emp", label: "Retirement contributions (employer required)" },
  { name: "month_exp_other_retr_volt", label: "Retirement contributions (voluntary)" },
  { name: "month_exp_other_union", label: "Union dues" },
  { name: "month_exp_other_unpaid_local", label: "Unpaid state/local taxes (min. payment)" },
  { name: "month_exp_other_stu_loan", label: "Student loans (min. payment)" },
];
const EXPENSE_OTHER: { type: keyof Form8857Data; val: keyof Form8857Data }[] = [
  { type: "month_exp_type1", val: "month_exp_type_val1" },
  { type: "month_exp_type2", val: "month_exp_type_val2" },
  { type: "month_exp_type3", val: "month_exp_type_val3" },
];

const assetColumns: Column<OtherAsset>[] = [
  { header: "Description", cell: (r) => r.description },
  { header: "Fair market value", cell: (r) => formatMoney(r.currentValue) },
  { header: "Loan balance", cell: (r) => formatMoney(r.loanBalance) },
];

const num = (v: unknown) => parseFloat(String(v ?? "").replace(/,/g, "")) || 0;
const money = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Part IV assets: the client's shared worksheet asset store (case-scoped). */
function AssetSection({ caseId }: { caseId: string }) {
  const assets = useCollection<OtherAsset>(caseId, otherAssetsApi.list);
  const [dialog, setDialog] = useState<{ open: boolean; record: OtherAsset | null }>({
    open: false,
    record: null,
  });

  return (
    <>
      <CollectionCard
        title="Assets"
        addLabel="Add Asset"
        columns={assetColumns}
        rows={assets.rows}
        loading={assets.loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(row) => setDialog({ open: true, record: row })}
        onDelete={(row) => otherAssetsApi.remove(caseId, row.id)}
      />
      <OtherAssetDialog
        caseId={caseId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={assets.reload}
      />
    </>
  );
}

/**
 * Part IV — Current financial situation (legacy `sr_part4.php`). Q20 assets are
 * the client's shared worksheet asset store (only when launched from a Case; the
 * plain-editor / client-scoped variant shows a nudge). Q21 household size and Q22
 * the monthly income/expense grid with client-side live totals + net.
 */
export function SrPart4({ caseId, onNext }: { caseId?: string; onNext: () => void }) {
  const { values } = useFormikContext<Form8857Data>();
  const incomeTotal = INCOME_VALUE_KEYS.reduce((sum, k) => sum + num(values[k]), 0);
  const expenseTotal = EXPENSE_VALUE_KEYS.reduce((sum, k) => sum + num(values[k]), 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="text-foreground text-sm font-medium">
          <span className="text-muted-foreground mr-2 font-mono">20</span>
          Your assets (money and property)
        </span>
        {caseId ? (
          <AssetSection caseId={caseId} />
        ) : (
          <Alert>
            <AlertTitle>Manage assets from a Case</AlertTitle>
            <AlertDescription>
              Assets are shared across the client's financial worksheets. Open this
              workflow from a Case to add, edit, and print assets on the 8857.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">21</span>
          How many people are you currently supporting, including yourself?
        </span>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="adults">Adults</Label>
            <FormikMoneyCell name="adults" integer aria-label="Adults" className="w-32 text-left" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="children">Children</Label>
            <FormikMoneyCell name="children" integer aria-label="Children" className="w-32 text-left" />
          </div>
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm font-medium">
          <span className="text-muted-foreground mr-2 font-mono">22</span>
          Average monthly income
        </span>
        {INCOME_ROWS.map((r) => (
          <div key={r.name} className="flex items-center justify-between gap-4">
            <Label htmlFor={r.name} className="font-normal">{r.label}</Label>
            <FormikMoneyCell name={r.name} aria-label={r.label} className="w-40" />
          </div>
        ))}
        {INCOME_OTHER.map((r, i) => (
          <div key={r.type} className="flex items-center gap-3">
            <FormikCell name={r.type} placeholder={`Other income type ${i + 1}`} className="flex-1" maxLength={50} />
            <FormikMoneyCell name={r.val} aria-label={`Other income amount ${i + 1}`} className="w-40" />
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 border-t pt-3 font-medium">
          <span>Total monthly income</span>
          <span className="tabular-nums" data-testid="income-total">{money(incomeTotal)}</span>
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm font-medium">Average monthly expenses</span>
        {EXPENSE_ROWS.map((r) => (
          <div key={r.name} className="flex items-center justify-between gap-4">
            <Label htmlFor={r.name} className="font-normal">{r.label}</Label>
            <FormikMoneyCell name={r.name} aria-label={r.label} className="w-40" />
          </div>
        ))}
        {EXPENSE_OTHER.map((r, i) => (
          <div key={r.type} className="flex items-center gap-3">
            <FormikCell name={r.type} placeholder={`Court-ordered debt type ${i + 1}`} className="flex-1" maxLength={50} />
            <FormikMoneyCell name={r.val} aria-label={`Court-ordered amount ${i + 1}`} className="w-40" />
          </div>
        ))}
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="month_exp_misc" className="font-normal">Miscellaneous</Label>
          <FormikMoneyCell name="month_exp_misc" aria-label="Miscellaneous" className="w-40" />
        </div>
        <div className="flex items-center justify-between gap-4 border-t pt-3 font-medium">
          <span>Total monthly expenses</span>
          <span className="tabular-nums" data-testid="expense-total">{money(expenseTotal)}</span>
        </div>
        <div className="flex items-center justify-between gap-4 font-medium">
          <span>Net monthly (income − expenses)</span>
          <span className="tabular-nums" data-testid="net-total">{money(incomeTotal - expenseTotal)}</span>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onNext}>
          Go to Part V <ArrowRight />
        </Button>
      </div>
    </div>
  );
}
